import React, { useState, useEffect } from 'react';
import { Order, OrderSummary, OrderStatus } from '../../../types';
import { orderService } from '../../../services/orderService';
import { sendOrderProcessingEmail } from '../../../services/orderEmailService';
import { OrderStatusBadge, PaymentStatusBadge, DateDisplay } from '../atoms';
import { formatFulfillmentWindow, getOrderFulfillmentTimes } from '../../../constants/fulfillmentWindows';
import './OrderDetailModal.css';

interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderSummary: OrderSummary | null;
  onOrderUpdate?: (order: Order) => void;
  onOrderDeleted?: () => void;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  orderSummary,
  onOrderUpdate,
  onOrderDeleted
}) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Time input states for collection/delivery times (using dropdowns)
  const [collectionStartHour, setCollectionStartHour] = useState<string>('');
  const [collectionStartMinute, setCollectionStartMinute] = useState<string>('00');
  const [collectionStartAmPm, setCollectionStartAmPm] = useState<string>('AM');
  const [collectionEndHour, setCollectionEndHour] = useState<string>('');
  const [collectionEndMinute, setCollectionEndMinute] = useState<string>('00');
  const [collectionEndAmPm, setCollectionEndAmPm] = useState<string>('PM');
  
  const [deliveryStartHour, setDeliveryStartHour] = useState<string>('');
  const [deliveryStartMinute, setDeliveryStartMinute] = useState<string>('00');
  const [deliveryStartAmPm, setDeliveryStartAmPm] = useState<string>('AM');
  const [deliveryEndHour, setDeliveryEndHour] = useState<string>('');
  const [deliveryEndMinute, setDeliveryEndMinute] = useState<string>('00');
  const [deliveryEndAmPm, setDeliveryEndAmPm] = useState<string>('PM');

  // Helper function to parse time string into hour, minute, and AM/PM
  const parseTimeString = (
    timeString: string,
    setters: { setHour: (val: string) => void; setMinute: (val: string) => void; setAmPm: (val: string) => void }
  ) => {
    if (!timeString) {
      setters.setHour('');
      setters.setMinute('00');
      setters.setAmPm('AM');
      return;
    }
    
    // Parse 12-hour format (e.g., "9:00 AM" or "5:30 PM")
    const match12 = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (match12) {
      setters.setHour(match12[1]);
      setters.setMinute(match12[2]);
      setters.setAmPm(match12[3].toUpperCase());
      return;
    }
    
    // Parse 24-hour format (e.g., "09:00" or "17:30")
    const match24 = timeString.match(/(\d+):(\d+)/);
    if (match24) {
      let hour = parseInt(match24[1], 10);
      const minute = match24[2];
      const ampm = hour >= 12 ? 'PM' : 'AM';
      if (hour > 12) hour -= 12;
      if (hour === 0) hour = 12;
      setters.setHour(hour.toString());
      setters.setMinute(minute);
      setters.setAmPm(ampm);
      return;
    }
    
    // Default if parsing fails
    setters.setHour('');
    setters.setMinute('00');
    setters.setAmPm('AM');
  };
  
  // Helper function to combine hour, minute, and AM/PM into time string
  const combineTimeString = (hour: string, minute: string, ampm: string): string => {
    if (!hour) return '';
    return `${hour}:${minute} ${ampm}`;
  };
  
  // Generate hour options (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  
  // Generate minute options (00, 15, 30, 45)
  const minuteOptions = ['00', '15', '30', '45'];

  // Fetch full order details when modal opens
  useEffect(() => {
    if (isOpen && orderSummary) {
      fetchOrderDetails();
    }
  }, [isOpen, orderSummary]);

  const fetchOrderDetails = async () => {
    if (!orderSummary) return;

    try {
      setLoading(true);
      setError(null);
      const fullOrder = await orderService.getOrder(orderSummary.id);
      setOrder(fullOrder);
      
      if (fullOrder) {
        // Initialize time inputs with existing values
        parseTimeString(fullOrder.collectionStartTime || '', {
          setHour: setCollectionStartHour,
          setMinute: setCollectionStartMinute,
          setAmPm: setCollectionStartAmPm
        });
        parseTimeString(fullOrder.collectionEndTime || '', {
          setHour: setCollectionEndHour,
          setMinute: setCollectionEndMinute,
          setAmPm: setCollectionEndAmPm
        });
        parseTimeString(fullOrder.deliveryStartTime || '', {
          setHour: setDeliveryStartHour,
          setMinute: setDeliveryStartMinute,
          setAmPm: setDeliveryStartAmPm
        });
        parseTimeString(fullOrder.deliveryEndTime || '', {
          setHour: setDeliveryEndHour,
          setMinute: setDeliveryEndMinute,
          setAmPm: setDeliveryEndAmPm
        });
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOrder(null);
    setError(null);
    onClose();
  };

  const handleDeleteOrder = async () => {
    if (!order) return;
    const confirmed = window.confirm(`Delete order ${order.orderNumber}? This is permanent.`);
    if (!confirmed) return;

    try {
      setDeleting(true);
      setError(null);

      await orderService.deleteOrder(order.id);
      onOrderDeleted?.();
      handleClose();
    } catch (err) {
      console.error('Failed to delete order:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusUpdate = async (newStatus: OrderStatus) => {
    if (!order) return;

    // Validate times if changing to processing
    if (newStatus === 'processing') {
      const isCollection = order.deliveryOption === 'collection';
      
      if (isCollection) {
        if (!collectionStartHour || !collectionEndHour) {
          setError('Collection start and end times are required before marking order as processing');
          return;
        }
      } else {
        if (!deliveryStartHour || !deliveryEndHour) {
          setError('Delivery start and end times are required before marking order as processing');
          return;
        }
      }
    }

    try {
      setUpdating(true);
      setError(null);
      
      // Prepare update data with times if changing to processing
      const updateData: any = {
        status: newStatus
      };
      
      if (newStatus === 'processing') {
        if (order.deliveryOption === 'collection') {
          updateData.collectionStartTime = combineTimeString(collectionStartHour, collectionStartMinute, collectionStartAmPm);
          updateData.collectionEndTime = combineTimeString(collectionEndHour, collectionEndMinute, collectionEndAmPm);
        } else {
          updateData.deliveryStartTime = combineTimeString(deliveryStartHour, deliveryStartMinute, deliveryStartAmPm);
          updateData.deliveryEndTime = combineTimeString(deliveryEndHour, deliveryEndMinute, deliveryEndAmPm);
        }
      }
      
      // Update order with status and times
      const updatedOrder = await orderService.updateOrder(order.id, updateData);
      
      // Send processing email if status is processing
      if (newStatus === 'processing') {
        const emailResult = await sendOrderProcessingEmail(updatedOrder);
        
        if (!emailResult.success) {
          // Revert the status update if email fails
          const revertedOrder = await orderService.updateOrder(order.id, {
            status: order.status
          });
          setOrder(revertedOrder);
          if (onOrderUpdate) {
            onOrderUpdate(revertedOrder);
          }
          setError(`Failed to send processing email: ${emailResult.error}. Order status was not updated.`);
          setUpdating(false);
          return;
        }
      }
      
      setOrder(updatedOrder);
      if (onOrderUpdate) {
        onOrderUpdate(updatedOrder);
      }
      
      // Show success message
      if (newStatus === 'processing') {
        alert('Order status updated to processing and email sent successfully!');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };
  
  const getNextStatusOptions = (currentStatus: OrderStatus): OrderStatus[] => {
    switch (currentStatus) {
      case 'pending':
        return ['confirmed', 'cancelled'];
      case 'confirmed':
        return ['processing', 'cancelled'];
      case 'processing':
        return ['ready_for_collection', 'out_for_delivery'];
      case 'ready_for_collection':
        return ['completed'];
      case 'out_for_delivery':
        return ['delivered', 'completed'];
      case 'delivered':
        return ['completed'];
      case 'completed':
        return [];
      case 'cancelled':
        return [];
      case 'refunded':
        return [];
      default:
        return [];
    }
  };

  const formatAddress = (address: any) => {
    if (!address) return 'N/A';
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (!isOpen) return null;

  return (
    <div className="order-detail-modal-overlay" onClick={handleClose}>
      <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {orderSummary ? `Order ${orderSummary.orderNumber}` : 'Order Details'}
          </h2>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-content">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading order details...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={fetchOrderDetails} className="retry-button">
                Retry
              </button>
            </div>
          )}

          {order && (
            <div className="order-details">
              {/* Order Status Section */}
              <div className="detail-section">
                <h3>Order Status</h3>
                <div className="status-grid">
                  <div className="status-item">
                    <label>Order Status:</label>
                    <OrderStatusBadge status={order.status} />
                  </div>
                  <div className="status-item">
                    <label>Payment Status:</label>
                    <PaymentStatusBadge status={order.payment.status} />
                  </div>
                  <div className="status-item">
                    <label>Order Number:</label>
                    <span className="order-number">{order.orderNumber}</span>
                  </div>
                  <div className="status-item">
                    <label>Created:</label>
                    <DateDisplay date={order.createdAt} format="long" />
                  </div>
                </div>

                {/* Order Actions */}
                <div className="order-actions">
                  <h4>Update Status</h4>

                  {order.status === 'cancelled' && (
                    <div className="order-delete-action">
                      <h5 className="order-delete-title">Danger Zone</h5>
                      <button
                        type="button"
                        className="order-details-action-button cancelled delete-order-btn"
                        onClick={handleDeleteOrder}
                        disabled={deleting}
                      >
                        {deleting ? 'Deleting…' : 'Delete cancelled order'}
                      </button>
                    </div>
                  )}
                  
                  {/* Time Input Section - Show when processing is a possible next status */}
                  {getNextStatusOptions(order.status).includes('processing') && (
                    <div className="time-input-section">
                      <h5 style={{ marginBottom: '12px', color: '#475569' }}>
                        {order.deliveryOption === 'collection' ? 'Collection' : 'Delivery'} Times
                        <span style={{ color: '#ef4444', marginLeft: '5px' }}>*</span>
                      </h5>
                      <div className="time-inputs-grid">
                        {order.deliveryOption === 'collection' ? (
                          <>
                            <div className="time-input-group">
                              <label htmlFor="collection-start-time">Start Time:</label>
                              <div className="time-dropdowns">
                                <select
                                  id="collection-start-hour"
                                  value={collectionStartHour}
                                  onChange={(e) => setCollectionStartHour(e.target.value)}
                                  className="time-dropdown"
                                >
                                  <option value="">Hour</option>
                                  {hourOptions.map(hour => (
                                    <option key={hour} value={hour}>{hour}</option>
                                  ))}
                                </select>
                                <select
                                  id="collection-start-minute"
                                  value={collectionStartMinute}
                                  onChange={(e) => setCollectionStartMinute(e.target.value)}
                                  className="time-dropdown"
                                >
                                  {minuteOptions.map(minute => (
                                    <option key={minute} value={minute}>{minute}</option>
                                  ))}
                                </select>
                                <select
                                  id="collection-start-ampm"
                                  value={collectionStartAmPm}
                                  onChange={(e) => setCollectionStartAmPm(e.target.value)}
                                  className="time-dropdown"
                                >
                                  <option value="AM">AM</option>
                                  <option value="PM">PM</option>
                                </select>
                              </div>
                            </div>
                            <div className="time-input-group">
                              <label htmlFor="collection-end-time">End Time:</label>
                              <div className="time-dropdowns">
                                <select
                                  id="collection-end-hour"
                                  value={collectionEndHour}
                                  onChange={(e) => setCollectionEndHour(e.target.value)}
                                  className="time-dropdown"
                                >
                                  <option value="">Hour</option>
                                  {hourOptions.map(hour => (
                                    <option key={hour} value={hour}>{hour}</option>
                                  ))}
                                </select>
                                <select
                                  id="collection-end-minute"
                                  value={collectionEndMinute}
                                  onChange={(e) => setCollectionEndMinute(e.target.value)}
                                  className="time-dropdown"
                                >
                                  {minuteOptions.map(minute => (
                                    <option key={minute} value={minute}>{minute}</option>
                                  ))}
                                </select>
                                <select
                                  id="collection-end-ampm"
                                  value={collectionEndAmPm}
                                  onChange={(e) => setCollectionEndAmPm(e.target.value)}
                                  className="time-dropdown"
                                >
                                  <option value="AM">AM</option>
                                  <option value="PM">PM</option>
                                </select>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="time-input-group">
                              <label htmlFor="delivery-start-time">Start Time:</label>
                              <div className="time-dropdowns">
                                <select
                                  id="delivery-start-hour"
                                  value={deliveryStartHour}
                                  onChange={(e) => setDeliveryStartHour(e.target.value)}
                                  className="time-dropdown"
                                >
                                  <option value="">Hour</option>
                                  {hourOptions.map(hour => (
                                    <option key={hour} value={hour}>{hour}</option>
                                  ))}
                                </select>
                                <select
                                  id="delivery-start-minute"
                                  value={deliveryStartMinute}
                                  onChange={(e) => setDeliveryStartMinute(e.target.value)}
                                  className="time-dropdown"
                                >
                                  {minuteOptions.map(minute => (
                                    <option key={minute} value={minute}>{minute}</option>
                                  ))}
                                </select>
                                <select
                                  id="delivery-start-ampm"
                                  value={deliveryStartAmPm}
                                  onChange={(e) => setDeliveryStartAmPm(e.target.value)}
                                  className="time-dropdown"
                                >
                                  <option value="AM">AM</option>
                                  <option value="PM">PM</option>
                                </select>
                              </div>
                            </div>
                            <div className="time-input-group">
                              <label htmlFor="delivery-end-time">End Time:</label>
                              <div className="time-dropdowns">
                                <select
                                  id="delivery-end-hour"
                                  value={deliveryEndHour}
                                  onChange={(e) => setDeliveryEndHour(e.target.value)}
                                  className="time-dropdown"
                                >
                                  <option value="">Hour</option>
                                  {hourOptions.map(hour => (
                                    <option key={hour} value={hour}>{hour}</option>
                                  ))}
                                </select>
                                <select
                                  id="delivery-end-minute"
                                  value={deliveryEndMinute}
                                  onChange={(e) => setDeliveryEndMinute(e.target.value)}
                                  className="time-dropdown"
                                >
                                  {minuteOptions.map(minute => (
                                    <option key={minute} value={minute}>{minute}</option>
                                  ))}
                                </select>
                                <select
                                  id="delivery-end-ampm"
                                  value={deliveryEndAmPm}
                                  onChange={(e) => setDeliveryEndAmPm(e.target.value)}
                                  className="time-dropdown"
                                >
                                  <option value="AM">AM</option>
                                  <option value="PM">PM</option>
                                </select>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="order-details-action-buttons">
                    {getNextStatusOptions(order.status).map((status) => (
                      <button
                        key={status}
                        className={`order-details-action-button ${status}`}
                        onClick={() => handleStatusUpdate(status)}
                        disabled={updating}
                      >
                        {updating ? 'Updating...' : `Mark as ${status.replace('_', ' ')}`}
                      </button>
                    ))}
                    {getNextStatusOptions(order.status).length === 0 && (
                      <span className="no-actions">No further actions available</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="detail-section">
                <h3>Customer Information</h3>
                <div className="customer-details">
                  <div className="customer-info-grid">
                    <div className="info-item">
                      <label>Name:</label>
                      <span>{order.customer.firstName} {order.customer.lastName}</span>
                    </div>
                    <div className="info-item">
                      <label>Email:</label>
                      <span>{order.customer.email}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone:</label>
                      <span>{order.customer.phone || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <label>Customer ID:</label>
                      <span className="customer-id">{order.customer.id}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div className="detail-section">
                <h3>Delivery Information</h3>
                <div className="delivery-details">
                  <div className="delivery-info-grid">
                    <div className="info-item">
                      <label>Delivery Option:</label>
                      <span className="delivery-option">
                        {order.deliveryOption === 'delivery' ? 'Delivery' : 'Collection'}
                      </span>
                    </div>
                    {order.deliveryDate && (
                      <div className="info-item">
                        <label>Delivery Date:</label>
                        <DateDisplay date={order.deliveryDate} format="long" />
                      </div>
                    )}
                    {(() => {
                      const { startTime, endTime } = getOrderFulfillmentTimes(order);
                      const timeWindow = formatFulfillmentWindow(startTime, endTime);
                      if (!timeWindow) return null;
                      return (
                        <div className="info-item">
                          <label>
                            {order.deliveryOption === 'delivery' ? 'Delivery window' : 'Collection window'}:
                          </label>
                          <span>{timeWindow}</span>
                        </div>
                      );
                    })()}
                    {order.deliveryAddress && (
                      <div className="info-item full-width">
                        <label>Delivery Address:</label>
                        <div className="address-block">
                          {formatAddress(order.deliveryAddress)}
                        </div>
                      </div>
                    )}
                    {order.deliveryInstructions && (
                      <div className="info-item full-width">
                        <label>Delivery Instructions:</label>
                        <div className="instructions-block">
                          {order.deliveryInstructions}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="detail-section">
                <h3>Order Items ({order.items.length})</h3>
                <div className="order-items">
                  {order.items.map((item, index) => (
                    <div key={item.id || index} className="order-item">
                        <div className="item-header">
                          <div className="item-image">
                            <img src={item.imageUrl} alt={item.name} />
                          </div>
                          <div className="item-details">
                            <h4>{item.name}</h4>
                            <div className="item-specs">
                              <span className="spec">Size: {item.size}</span>
                              <span className="spec">Shape: {item.shape}</span>
                              <span className="spec">Layers: {item.layers}</span>
                            </div>
                          </div>
                          <div className="item-price">
                            <span className="unit-price">€{item.unitPrice.toFixed(2)}</span>
                            <span className="quantity">Qty: {item.quantity}</span>
                            <span className="total-price">€{item.totalPrice.toFixed(2)}</span>
                          </div>
                        </div>
                        
                        <div className="item-customizations">
                          <div className="customization-row">
                            <span className="label">Flavors:</span>
                            <span className="value">{item.flavors.join(', ')}</span>
                          </div>
                          <div className="customization-row">
                            <span className="label">Filling:</span>
                            <span className="value">{item.filling}</span>
                          </div>
                          <div className="customization-row">
                            <span className="label">Icing:</span>
                            <span className="value">{item.icing}</span>
                          </div>
                          <div className="customization-row">
                            <span className="label">Base Color:</span>
                            <span className="value">{item.baseColor}</span>
                          </div>
                          <div className="customization-row">
                            <span className="label">Piping Color:</span>
                            <span className="value">{item.pipingColor}</span>
                          </div>
                          {item.addOns && item.addOns.length > 0 && (
                            <div className="customization-row">
                              <span className="label">Add-ons:</span>
                              <span className="value">{item.addOns.join(', ')}</span>
                            </div>
                          )}
                          {item.matchingCupcakes && item.matchingCupcakes.length > 0 && (
                            <div className="customization-row">
                              <span className="label">Matching Cupcakes:</span>
                              <span className="value">{item.matchingCupcakes.join(', ')}</span>
                            </div>
                          )}
                          {item.specialInstructions && (
                            <div className="customization-row full-width">
                              <span className="label">Message on cake:</span>
                              <span className="value">{item.specialInstructions}</span>
                            </div>
                          )}
                          {item.hasImageOnCake && item.cakeImageUrl && (
                            <div className="customization-row full-width">
                              <span className="label">Image on cake:</span>
                              <div className="value">
                                <img 
                                  src={item.cakeImageUrl} 
                                  alt="Cake image" 
                                  style={{ maxWidth: '150px', maxHeight: '150px', borderRadius: '8px', marginTop: '8px' }}
                                />
                              </div>
                            </div>
                          )}
                          {item.hasSampleImage && item.sampleImageUrl && (
                            <div className="customization-row full-width">
                              <span className="label">Sample cake image:</span>
                              <div className="value">
                                <img 
                                  src={item.sampleImageUrl} 
                                  alt="Sample cake image" 
                                  style={{ maxWidth: '150px', maxHeight: '150px', borderRadius: '8px', marginTop: '8px' }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="detail-section">
                <h3>Order Summary</h3>
                <div className="order-summary">
                  <div className="summary-row">
                    <span>Subtotal:</span>
                    <span>€{order.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>VAT ({order.vatPercentage}%):</span>
                    <span>€{order.vat.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Shipping:</span>
                    <span>€{order.shipping.toFixed(2)}</span>
                  </div>
                  {order.coupon && order.couponDiscount > 0 && (
                    <>
                      <div className="summary-row">
                        <span>Subtotal + VAT + Shipping:</span>
                        <span>€{order.originalTotal.toFixed(2)}</span>
                      </div>
                      <div className="summary-row discount">
                        <span>Coupon Discount ({order.coupon.code}):</span>
                        <span>-€{order.couponDiscount.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="summary-row total">
                    <span>Total:</span>
                    <span>€{order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="detail-section">
                <h3>Payment Information</h3>
                <div className="payment-details">
                  <div className="payment-info-grid">
                    <div className="info-item">
                      <label>Payment Method:</label>
                      <span>{order.payment.method}</span>
                    </div>
                    <div className="info-item">
                      <label>Amount:</label>
                      <span>€{order.payment.amount.toFixed(2)}</span>
                    </div>
                    <div className="info-item">
                      <label>Currency:</label>
                      <span>{order.payment.currency.toUpperCase()}</span>
                    </div>
                    <div className="info-item">
                      <label>Payment ID:</label>
                      <span className="payment-id">{order.payment.id}</span>
                    </div>
                    <div className="info-item">
                      <label>Payment Date:</label>
                      <DateDisplay date={order.payment.createdAt} format="long" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="detail-section">
                <h3>Admin Notes</h3>
                <div className="admin-notes">
                  <p>{order.notes || 'No admin notes for this order.'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
